import { Component, OnDestroy, OnInit } from '@angular/core';
import QRCode from 'qrcode';
import { PlusAuthenticationService, UserService } from '@karve.it/core';
import { RawUser } from '@karve.it/interfaces/auth';
import { SubSink } from 'subsink';
import { HttpClient } from '@angular/common/http';
import { Apollo } from 'apollo-angular';
import { gql } from '@apollo/client/core';
import { ArtifactService } from 'src/app/core/artifact/artifact.service';
import { FreyaNotificationsService } from 'src/app/services/freya-notifications.service';

interface FAAccountInfo {
	user: {
		id: string;
		email: string;
		firstName?: string;
		lastName?: string;
		mobilePhone?: string;
		twoFactorEnabled?: boolean;
		twoFactorDelivery?: string;
		active?: boolean;
		verified?: boolean;
		data?: Record<string, unknown>;
	};
}

@Component({
	selector: 'app-account-page',
	templateUrl: './account-page.component.html',
	styleUrls: ['./account-page.component.scss'],
})
export class AccountPageComponent implements OnInit, OnDestroy {

	subs = new SubSink();

	user: RawUser;
	faAccount: FAAccountInfo | null = null;
	loadingAccount = true;
	accountError = '';

	avatarUrl: string | null = null;
	uploadingAvatar = false;

	// Edit profile dialog
	editingProfile = false;
	profileForm = { firstName: '', lastName: '', mobilePhone: '' };
	savingProfile = false;

	// Change password dialog
	changingPassword = false;
	passwordForm = { currentPassword: '', newPassword: '', confirmPassword: '' };
	savingPassword = false;

	// 2FA
	enableTwoFactorDialog = false;
	twoFactorSecret = '';
	twoFactorQrCode = '';
	twoFactorCode = '';
	enrollingTwoFactor = false;
	twoFactorQrDataUrl = '';

	// Passkeys
	passkeys: { id: string; name?: string; createdAt?: string }[] = [];
	loadingPasskeys = false;

	// Sessions
	sessions: { id: string; startInstant?: string; expirationInstant?: string }[] = [];
	loadingSessions = false;

	// Re-auth state
	recentlyAuthed = false;
	showReAuthDialog = false;
	reAuthPassword = '';
	reAuthLoading = false;
	pendingAction: (() => void) | null = null;

	constructor(
		private userService: UserService,
		private plusAuth: PlusAuthenticationService,
		private http: HttpClient,
		private apollo: Apollo,
		private artifactService: ArtifactService,
		private localNotify: FreyaNotificationsService,
	) { }

	ngOnInit(): void {
		this.subs.sink = this.userService.listUsersV2({
			userIds: [this.plusAuth.user.id],
		}, { twoFactorEnabled: true, roles: true }).subscribe((res) => {
			this.user = res.data.usersv2.users[0];
			this.avatarUrl = (this.user as any)?.avatar || null;
		});

		this.loadAccountInfo();
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
	}

	private loadAccountInfo(): void {
		this.loadingAccount = true;
		this.http.get<FAAccountInfo>('/api/auth/account').subscribe({
			next: (res) => {
				this.faAccount = res;
				this.loadingAccount = false;
				this.loadPasskeys();
				this.loadSessions();
				this.checkAuthFreshness();
			},
			error: (err) => {
				this.accountError = err?.error?.error || 'Unable to load account details.';
				this.loadingAccount = false;
			},
		});
	}

	private loadSessions(): void {
		this.loadingSessions = true;
		this.http.get<{ sessions: any[] }>('/api/auth/sessions').subscribe({
			next: (res) => {
				this.sessions = res.sessions || [];
				this.loadingSessions = false;
			},
			error: () => {
				this.loadingSessions = false;
			},
		});
	}

	private checkAuthFreshness(): void {
		const val = (this.plusAuth.user as any)?.val || 0;
		const now = Math.floor(Date.now() / 1000);
		this.recentlyAuthed = (now - val) < 900; // 15 minutes
	}

	revokeSession(sessionId: string): void {
		if (!this.recentlyAuthed) {
			this.pendingAction = () => this.doRevokeSession(sessionId);
			this.showReAuthDialog = true;
			return;
		}
		this.doRevokeSession(sessionId);
	}

	private doRevokeSession(sessionId: string): void {
		this.http.delete(`/api/auth/sessions/${sessionId}`).subscribe({
			next: () => {
				this.sessions = this.sessions.filter((s) => s.id !== sessionId);
				this.localNotify.addToast.next({ severity: 'success', summary: 'Session revoked' });
			},
			error: (err) => {
				if (err?.error?.code === 'RE_AUTH_REQUIRED') {
					this.pendingAction = () => this.doRevokeSession(sessionId);
					this.showReAuthDialog = true;
				} else {
					this.localNotify.addToast.next({ severity: 'error', summary: err?.error?.error || 'Failed to revoke session' });
				}
			},
		});
	}

	confirmReAuth(): void {
		if (!this.reAuthPassword) { return; }
		this.reAuthLoading = true;
		this.http.post('/api/auth/re-auth', { password: this.reAuthPassword }).subscribe({
			next: () => {
				this.reAuthLoading = false;
				this.recentlyAuthed = true;
				this.showReAuthDialog = false;
				this.reAuthPassword = '';
				const action = this.pendingAction;
				this.pendingAction = null;
				if (action) { action(); }
			},
			error: (err) => {
				this.reAuthLoading = false;
				this.localNotify.addToast.next({
					severity: 'error',
					summary: err?.error?.error || 'Re-authentication failed',
				});
			},
		});
	}

	cancelReAuth(): void {
		this.showReAuthDialog = false;
		this.reAuthPassword = '';
		this.pendingAction = null;
	}

	private loadPasskeys(): void {
		this.loadingPasskeys = true;
		this.http.get<{ credentials: { id: string; name?: string; createdAt?: string }[] }>('/api/auth/passkeys').subscribe({
			next: (res) => {
				this.passkeys = res.credentials || [];
				this.loadingPasskeys = false;
			},
			error: () => {
				this.loadingPasskeys = false;
			},
		});
	}

	deletePasskey(credentialId: string): void {
		if (!confirm('Remove this passkey?')) { return; }
		this.http.delete(`/api/auth/passkeys/${credentialId}`).subscribe({
			next: () => {
				this.passkeys = this.passkeys.filter((p) => p.id !== credentialId);
				this.localNotify.addToast.next({ severity: 'success', summary: 'Passkey removed' });
			},
			error: (err) => {
				this.localNotify.addToast.next({ severity: 'error', summary: err?.error?.error || 'Failed to remove passkey' });
			},
		});
	}

	onAvatarSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		if (!file) { return; }

		if (!file.type.startsWith('image/')) {
			this.localNotify.addToast.next({ severity: 'error', summary: 'Please select an image file' });
			return;
		}

		if (file.size > 5 * 1024 * 1024) {
			this.localNotify.addToast.next({ severity: 'error', summary: 'Image must be under 5MB' });
			return;
		}

		this.uploadingAvatar = true;
		const userId = this.plusAuth.user.id;

		const ext = file.type.split('/')[1] || 'jpg';
		this.subs.sink = this.artifactService.createArtifact([{
			name: `avatar-${userId}.${ext}`,
			namespace: 'moveright',
			fileSize: file.size,
			contentType: file.type,
			public: true,
			relatedObjectIds: [userId],
			author: userId,
		}]).subscribe({
			next: (res: any) => {
				const artifact = res?.data?.createArtifacts?.artifacts?.[0];
				if (!artifact?.signedPolicy) {
						this.localNotify.addToast.next({ severity: 'error', summary: 'Avatar upload failed — service unavailable' });
					this.uploadingAvatar = false;
					return;
				}

				this.artifactService.uploadFile(file as any, JSON.parse(artifact.signedPolicy)).subscribe({
					next: () => {
						const publicUrl = artifact.url || artifact.signedUrl;
						this.saveAvatarUrl(publicUrl);
					},
					error: (err) => {
						const detail = err?.status === 0
							? 'Network error — check that the GCS bucket is configured'
							: 'Failed to upload image to storage';
						this.localNotify.addToast.next({ severity: 'error', summary: detail });
						this.uploadingAvatar = false;
					},
				});
			},
			error: (err) => {
				const detail = err?.message?.includes('Artifact Service disabled')
					? 'Artifact storage is not configured on this server'
					: 'Failed to create avatar';
				this.localNotify.addToast.next({ severity: 'error', summary: detail });
				this.uploadingAvatar = false;
			},
		});

		input.value = '';
	}

	private saveAvatarUrl(url: string): void {
		this.subs.sink = this.apollo.mutate({
			mutation: gql`
				mutation setAvatar($id: String!, $avatar: String) {
					users(filter: { userIds: [$id], showDeactivated: true }) {
						setProfile(avatar: $avatar)
					}
				}
			`,
			variables: {
				id: this.plusAuth.user.id,
				avatar: url,
			},
		}).subscribe({
			next: () => {
				this.avatarUrl = url;
				this.uploadingAvatar = false;
				this.localNotify.addToast.next({ severity: 'success', summary: 'Avatar updated' });
			},
			error: () => {
				this.localNotify.addToast.next({ severity: 'error', summary: 'Failed to save avatar' });
				this.uploadingAvatar = false;
			},
		});
	}

	// ── Edit Profile ────────────────────────────────────────────────────

	startEditProfile(): void {
		this.profileForm = {
			firstName: this.faAccount?.user?.firstName || this.user?.givenName || '',
			lastName: this.faAccount?.user?.lastName || this.user?.familyName || '',
			mobilePhone: this.faAccount?.user?.mobilePhone || this.user?.phone || '',
		};
		this.editingProfile = true;
	}

	saveProfile(): void {
		this.savingProfile = true;
		this.http.patch('/api/auth/profile', this.profileForm).subscribe({
			next: (res: any) => {
				if (this.faAccount?.user) {
					this.faAccount.user.firstName = res.user?.firstName ?? this.profileForm.firstName;
					this.faAccount.user.lastName = res.user?.lastName ?? this.profileForm.lastName;
					this.faAccount.user.mobilePhone = res.user?.mobilePhone ?? this.profileForm.mobilePhone;
				}
				if (this.user) {
					this.user.givenName = res.user?.firstName ?? this.profileForm.firstName;
					this.user.familyName = res.user?.lastName ?? this.profileForm.lastName;
					this.user.phone = res.user?.mobilePhone ?? this.profileForm.mobilePhone;
				}
				this.editingProfile = false;
				this.savingProfile = false;
				this.localNotify.addToast.next({ severity: 'success', summary: 'Profile updated' });
			},
			error: (err) => {
				this.savingProfile = false;
				this.localNotify.addToast.next({ severity: 'error', summary: err?.error?.error || 'Failed to update profile' });
			},
		});
	}

	cancelEditProfile(): void {
		this.editingProfile = false;
	}

	// ── Change Password ──────────────────────────────────────────────────

	startChangePassword(): void {
		if (!this.recentlyAuthed) {
			this.pendingAction = () => this.openChangePasswordDialog();
			this.showReAuthDialog = true;
			return;
		}
		this.openChangePasswordDialog();
	}

	private openChangePasswordDialog(): void {
		this.passwordForm = { currentPassword: '', newPassword: '', confirmPassword: '' };
		this.changingPassword = true;
	}

	savePassword(): void {
		if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
			this.localNotify.addToast.next({ severity: 'error', summary: 'Passwords do not match' });
			return;
		}
		if (this.passwordForm.newPassword.length < 8) {
			this.localNotify.addToast.next({ severity: 'error', summary: 'Password must be at least 8 characters' });
			return;
		}
		this.savingPassword = true;
		this.http.post('/api/auth/change-password', {
			currentPassword: this.passwordForm.currentPassword,
			newPassword: this.passwordForm.newPassword,
		}).subscribe({
			next: () => {
				this.changingPassword = false;
				this.savingPassword = false;
				this.localNotify.addToast.next({ severity: 'success', summary: 'Password changed' });
			},
			error: (err) => {
				this.savingPassword = false;
				if (err?.error?.code === 'RE_AUTH_REQUIRED') {
					this.pendingAction = () => this.savePassword();
					this.showReAuthDialog = true;
				} else {
					this.localNotify.addToast.next({ severity: 'error', summary: err?.error?.error || 'Failed to change password' });
				}
			},
		});
	}

	cancelChangePassword(): void {
		this.changingPassword = false;
	}

	// ── Two-Factor Authentication ────────────────────────────────────────

	startEnableTwoFactor(): void {
		this.enrollingTwoFactor = true;
		this.http.post('/api/auth/two-factor/enable', {}).subscribe({
			next: async (res: any) => {
				this.twoFactorSecret = res.secret || '';
				this.twoFactorQrCode = res.otpauthUri || '';
				this.twoFactorCode = '';
				this.twoFactorQrDataUrl = await QRCode.toDataURL(this.twoFactorQrCode, { width: 200 });
				this.enableTwoFactorDialog = true;
				this.enrollingTwoFactor = false;
			},
			error: (err) => {
				this.enrollingTwoFactor = false;
				this.localNotify.addToast.next({ severity: 'error', summary: err?.error?.error || 'Failed to start 2FA enrollment' });
			},
		});
	}

	confirmTwoFactor(): void {
		if (!this.twoFactorCode) {
			this.localNotify.addToast.next({ severity: 'error', summary: 'Enter the verification code' });
			return;
		}
		this.enrollingTwoFactor = true;
		this.http.post('/api/auth/two-factor/verify', {
			secret: this.twoFactorSecret,
			code: this.twoFactorCode,
		}).subscribe({
			next: (res: any) => {
				if (this.faAccount?.user) {
					this.faAccount.user.twoFactorEnabled = true;
				}
				this.enableTwoFactorDialog = false;
				this.enrollingTwoFactor = false;
				const codes = res.recoveryCodes?.join(', ') || '';
				this.localNotify.addToast.next({
					severity: 'success',
					summary: 'Two-factor authentication enabled'
						+ (codes ? `. Recovery codes: ${codes}` : ''),
				});
			},
			error: (err) => {
				this.enrollingTwoFactor = false;
				this.localNotify.addToast.next({ severity: 'error', summary: err?.error?.error || 'Verification failed' });
			},
		});
	}

	cancelTwoFactor(): void {
		this.enableTwoFactorDialog = false;
		this.twoFactorSecret = '';
		this.twoFactorQrCode = '';
		this.twoFactorCode = '';
		this.twoFactorQrDataUrl = '';
	}

	disableTwoFactor(): void {
		if (!confirm('Disable two-factor authentication?')) { return; }
		if (!this.recentlyAuthed) {
			this.pendingAction = () => this.doDisableTwoFactor();
			this.showReAuthDialog = true;
			return;
		}
		this.doDisableTwoFactor();
	}

	private doDisableTwoFactor(): void {
		this.http.post('/api/auth/two-factor/disable', {}).subscribe({
			next: () => {
				if (this.faAccount?.user) {
					this.faAccount.user.twoFactorEnabled = false;
				}
				this.localNotify.addToast.next({ severity: 'success', summary: 'Two-factor authentication disabled' });
			},
			error: (err) => {
				if (err?.error?.code === 'RE_AUTH_REQUIRED') {
					this.pendingAction = () => this.doDisableTwoFactor();
					this.showReAuthDialog = true;
				} else {
					this.localNotify.addToast.next({ severity: 'error', summary: err?.error?.error || 'Failed to disable 2FA' });
				}
			},
		});
	}

	get fullName(): string {
		const given = this.user?.givenName || this.faAccount?.user?.firstName || '';
		const family = this.user?.familyName || this.faAccount?.user?.lastName || '';
		return `${given} ${family}`.trim() || this.user?.email || this.faAccount?.user?.email || '';
	}

	get initials(): string {
		const given = (this.user?.givenName || this.faAccount?.user?.firstName || '').trim();
		const family = (this.user?.familyName || this.faAccount?.user?.lastName || '').trim();
		if (given && family) {
			return (given[0] + family[0]).toUpperCase();
		}
		if (given) { return given[0].toUpperCase(); }
		const email = this.user?.email || this.faAccount?.user?.email || '';
		return email ? email[0].toUpperCase() : '?';
	}

	get email(): string {
		return this.user?.email || this.faAccount?.user?.email || '';
	}

	get phone(): string {
		return this.user?.phone || this.faAccount?.user?.mobilePhone || '';
	}

	get twoFactorEnabled(): boolean {
		return this.user?.twoFactorEnabled || this.faAccount?.user?.twoFactorEnabled || false;
	}

	get roles(): string[] {
		return this.user?.roles?.map((r: any) => r.name) || [];
	}

	get year(): number {
		return new Date().getFullYear();
	}
}